/**
 * ValueProvenanceLine
 *
 * Compact display of value provenance: who set/accepted, when, method, note.
 * Only shows fields that are present. Uses existing data from value_proposals / accepted_values.
 * Automatically resolves UUID user IDs to display names via useSystemUsers.
 */

import { useMemo } from 'react';
import { useActorDisplayMap } from '../../hooks/prioritization/useActorDisplayMap';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface ValueProvenanceLineProps {
  /** Display value (optional; provenance can be shown without repeating value) */
  value?: unknown;
  /** Who accepted/set the value (user ID or display name) */
  acceptedBy?: string | null;
  /** When the value was accepted */
  acceptedAt?: Date | string | null;
  /** How it was accepted (e.g. facilitator_override, consensus) */
  acceptanceMethod?: string | null;
  /** Optional note from acceptance */
  acceptanceNote?: string | null;
  /** Who proposed (if from a proposal); optional (user ID or display name) */
  proposedBy?: string | null;
  /** When proposed; optional */
  proposedAt?: Date | string | null;
  /** Confidence 0–1 or 0–100; optional */
  confidence?: number | null;
  /** Source label (e.g. evidence); optional */
  sourceLabel?: string | null;
  /** Proposal note / rationale; optional */
  proposalNote?: string | null;
  /** Single-line style when true */
  compact?: boolean;
  className?: string;
}

function formatDate(d: Date | string): string {
  const date = typeof d === 'string' ? new Date(d) : d;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const ACCEPTANCE_METHOD_LABELS: Record<string, string> = {
  facilitator_override: 'Facilitator decision',
  facilitator_decision: 'Facilitator decision',
  consensus: 'Consensus',
  vote: 'Vote',
  authority: 'Authority',
};

function formatMethod(m: string): string {
  const key = String(m).trim().toLowerCase();
  if (ACCEPTANCE_METHOD_LABELS[key]) return ACCEPTANCE_METHOD_LABELS[key];
  const normalized = m.replace(/_/g, ' ');
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

export function ValueProvenanceLine({
  value: _value,
  acceptedBy: rawAcceptedBy,
  acceptedAt,
  acceptanceMethod,
  acceptanceNote,
  proposedBy: rawProposedBy,
  proposedAt,
  confidence,
  sourceLabel,
  proposalNote,
  compact = false,
  className = '',
}: ValueProvenanceLineProps) {
  const idsToResolve = useMemo(() => {
    const ids: string[] = [];
    if (rawAcceptedBy && UUID_RE.test(rawAcceptedBy)) ids.push(rawAcceptedBy);
    if (rawProposedBy && UUID_RE.test(rawProposedBy)) ids.push(rawProposedBy);
    return ids;
  }, [rawAcceptedBy, rawProposedBy]);

  const actorMap = useActorDisplayMap(idsToResolve);

  const resolve = (id: string | null | undefined): string | null | undefined => {
    if (!id || !UUID_RE.test(id)) return id;
    return actorMap[id] ?? id;
  };

  const acceptedBy = resolve(rawAcceptedBy);
  const proposedBy = resolve(rawProposedBy);

  const hasAccepted = acceptedBy != null || acceptedAt != null;
  const hasProposed = proposedBy != null || proposedAt != null;
  const hasMethod = acceptanceMethod != null && String(acceptanceMethod).trim() !== '';
  const hasNote = acceptanceNote != null && String(acceptanceNote).trim() !== '';
  const hasProposalNote = proposalNote != null && String(proposalNote).trim() !== '';
  const hasConfidence = confidence != null && !Number.isNaN(Number(confidence));
  const hasSource = sourceLabel != null && String(sourceLabel).trim() !== '';

  if (!hasAccepted && !hasProposed && !hasMethod && !hasNote && !hasProposalNote && !hasConfidence && !hasSource) {
    return null;
  }

  const confidenceDisplay = hasConfidence
    ? Number(confidence) <= 1
      ? `${Math.round(Number(confidence) * 100)}%`
      : `${Math.round(Number(confidence))}%`
    : null;

  if (compact) {
    const parts: string[] = [];
    if (proposedBy) parts.push(`Proposed by ${proposedBy}`);
    if (proposedAt) parts.push(formatDate(proposedAt));
    if (acceptedBy) parts.push(`Accepted by ${acceptedBy}`);
    if (acceptedAt) parts.push(formatDate(acceptedAt));
    if (hasMethod) parts.push(`Method: ${formatMethod(acceptanceMethod!)}`);
    if (confidenceDisplay) parts.push(`Confidence: ${confidenceDisplay}`);
    if (hasSource) parts.push(sourceLabel!);
    return (
      <p className={`text-xs text-[var(--text-ter)] m-0 leading-snug ${className}`.trim()}>
        {parts.join(' · ')}
        {(hasNote || hasProposalNote) && parts.length > 0 && ' · '}
        {hasNote && <span className="italic">{acceptanceNote}</span>}
        {hasNote && hasProposalNote && ' '}
        {hasProposalNote && <span className="italic">{proposalNote}</span>}
      </p>
    );
  }

  return (
    <div className={`text-xs text-[var(--text-sec)] space-y-0.5 ${className}`.trim()}>
      {(proposedBy || proposedAt) && (
        <p>
          {proposedBy && <span>Proposed by {proposedBy}</span>}
          {proposedBy && proposedAt && ' · '}
          {proposedAt && <span>{formatDate(proposedAt)}</span>}
        </p>
      )}
      {(acceptedBy || acceptedAt) && (
        <p>
          {acceptedBy && <span>Accepted by {acceptedBy}</span>}
          {acceptedBy && acceptedAt && ' · '}
          {acceptedAt && <span>Accepted {formatDate(acceptedAt)}</span>}
        </p>
      )}
      {hasMethod && <p>Method: {formatMethod(acceptanceMethod!)}</p>}
      {confidenceDisplay != null && <p>Confidence: {confidenceDisplay}</p>}
      {hasSource && <p>Source: {sourceLabel}</p>}
      {hasProposalNote && <p className="italic">{proposalNote}</p>}
      {hasNote && <p className="italic">{acceptanceNote}</p>}
    </div>
  );
}

export default ValueProvenanceLine;
